import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';
import { environment } from '../../../../../environments/environment';

interface UnitOption {
  value: string;
  label: string;
}

const VALID_TYPES = ['apartment', 'villa', 'studio', 'chalet', 'shop', 'office', 'garage', 'warehouse'];

const TYPE_LABELS: Record<string, string> = {
  apartment: 'شقة (Apartment)',
  villa: 'فيلا (Villa)',
  studio: 'استوديو (Studio)',
  chalet: 'شاليه (Chalet)',
  shop: 'متجر / محل (Shop)',
  office: 'مكتب (Office)',
  garage: 'كراج (Garage)',
  warehouse: 'مستودع (Warehouse)',
};

@Component({
  selector: 'app-contact-form',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div
      id="contactFormCard"
      class="surface-card rounded-3xl p-6 md:p-8 border border-border-strong shadow-modal antialiased relative overflow-hidden"
    >
      <div
        class="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none"
      ></div>

      <div class="text-center space-y-1 mb-6 relative z-10">
        <h3 class="text-text-primary text-base font-black tracking-tight uppercase font-mono">
          {{ 'projectsDetails.requestInfo' | translate }}
        </h3>
        <p class="text-[11px] text-text-muted font-medium">
          {{ 'projectsDetails.submitDetails' | translate }}
        </p>
      </div>

      <!-- Success Alert -->
      @if (successMessage) {
        <div class="mb-4 p-4 bg-success-soft border border-success-border text-success text-xs font-semibold rounded-2xl flex items-center gap-2.5 animate-fadeIn">
          <i class="fa-solid fa-circle-check text-sm shrink-0"></i>
          <span>{{ successMessage }}</span>
        </div>
      }

      <!-- Error Alert -->
      @if (errorMessage) {
        <div class="mb-4 p-4 bg-danger-soft border border-danger-border text-danger text-xs font-semibold rounded-2xl flex items-center gap-2.5 animate-fadeIn">
          <i class="fa-solid fa-circle-exclamation text-sm shrink-0"></i>
          <span>{{ errorMessage }}</span>
        </div>
      }

      <form class="space-y-4 relative z-10" (ngSubmit)="onSubmit()">
        <div class="relative">
          <div
            class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-text-muted"
          >
            <i class="fa-regular fa-user text-xs"></i>
          </div>
          <input
            id="contactNameInput"
            type="text"
            [(ngModel)]="formData.name"
            name="name"
            required
            [placeholder]="'projectsDetails.fullName' | translate"
            class="w-full pl-10 pr-4 py-3 bg-surface-input border border-border-strong rounded-xl text-xs font-semibold text-text-primary placeholder-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all duration-200"
          />
        </div>

        <div class="relative">
          <div
            class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-text-muted"
          >
            <i class="fa-solid fa-phone-volume text-[11px]"></i>
          </div>
          <input
            id="contactPhoneInput"
            type="tel"
            [(ngModel)]="formData.phone"
            name="phone"
            required
            [placeholder]="'projectsDetails.phoneNumber' | translate"
            dir="ltr"
            class="w-full pl-10 pr-4 py-3 bg-surface-input border border-border-strong rounded-xl text-xs font-mono font-bold text-text-primary placeholder-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all duration-200 text-left"
          />
        </div>

        <div class="relative">
          <div
            class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-text-muted"
          >
            <i class="fa-regular fa-envelope text-xs"></i>
          </div>
          <input
            type="email"
            [(ngModel)]="formData.email"
            name="email"
            [placeholder]="'projectsDetails.emailAddress' | translate"
            class="w-full pl-10 pr-4 py-3 bg-surface-input border border-border-strong rounded-xl text-xs font-semibold text-text-primary placeholder-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all duration-200"
          />
        </div>

        <div class="relative">
          <div
            class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-text-muted"
          >
            <i class="fa-solid fa-house-laptop text-xs"></i>
          </div>
          <select
            [(ngModel)]="formData.unitType"
            name="unitType"
            class="w-full pl-10 pr-10 py-3 bg-surface-input border border-border-strong rounded-xl text-xs font-semibold text-text-primary focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all duration-200 appearance-none cursor-pointer"
          >
            <option value="" class="bg-surface text-text-muted">{{ 'projectsDetails.unitTypeInterest' | translate }}</option>
            @for (opt of optionsList; track opt.value) {
              <option [value]="opt.value" class="bg-surface text-text-primary">{{ opt.label }}</option>
            }
          </select>
          <div
            class="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-text-muted"
          >
            <i class="fa-solid fa-chevron-down text-[10px]"></i>
          </div>
        </div>

        <button
          type="submit"
          [disabled]="isSubmitting"
          class="btn btn-primary w-full flex items-center justify-center gap-2 px-6 py-3.5 text-xs font-black uppercase tracking-wider font-mono rounded-xl shadow-glow transition-all duration-200 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          @if (isSubmitting) {
            <i class="fa-solid fa-spinner fa-spin text-xs"></i>
            <span>جاري الإرسال...</span>
          } @else {
            <span>{{ 'projectsDetails.sendSecureRequest' | translate }}</span>
            <i class="fa-solid fa-arrow-right-long text-[10px] tracking-normal"></i>
          }
        </button>
      </form>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ContactFormComponent implements OnChanges {
  @Input() projectId: string = '';
  @Input() unitTypes: string[] = [];

  optionsList: UnitOption[] = [];

  formData = {
    name: '',
    phone: '',
    email: '',
    unitType: 'apartment',
  };

  isSubmitting = false;
  successMessage = '';
  errorMessage = '';

  constructor(private http: HttpClient) {
    this.updateOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unitTypes']) {
      this.updateOptions();
    }
  }

  private updateOptions(): void {
    if (this.unitTypes && this.unitTypes.length > 0) {
      const mapped = new Set<string>();
      this.unitTypes.forEach((u) => {
        const norm = this.normalizeUnitType(u);
        mapped.add(norm);
      });

      this.optionsList = Array.from(mapped).map((val) => ({
        value: val,
        label: TYPE_LABELS[val] || val,
      }));
    } else {
      this.optionsList = VALID_TYPES.map((val) => ({
        value: val,
        label: TYPE_LABELS[val],
      }));
    }

    if (!this.formData.unitType && this.optionsList.length > 0) {
      this.formData.unitType = this.optionsList[0].value;
    }
  }

  private normalizeUnitType(input?: string): string {
    if (!input) return 'apartment';
    const val = input.toLowerCase().trim();
    for (const t of VALID_TYPES) {
      if (val.includes(t)) return t;
    }
    return 'apartment';
  }

  onSubmit() {
    if (!this.formData.name.trim()) {
      this.errorMessage = 'يرجى كتابة الاسم الكامل';
      return;
    }
    if (!this.formData.phone.trim()) {
      this.errorMessage = 'يرجى كتابة رقم الهاتف للتواصل';
      return;
    }
    if (!this.projectId) {
      this.errorMessage = 'معرف المشروع غير متوفر';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const selectedUnitType = this.normalizeUnitType(this.formData.unitType);

    const payload = {
      name: this.formData.name.trim(),
      phoneNumber: this.formData.phone.trim(),
      email: this.formData.email.trim() || undefined,
      unitType: selectedUnitType,
      projectId: this.projectId,
    };

    this.http.post<any>(`${environment.api.baseUrl}/inquiry`, payload).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        this.successMessage = res?.message || 'تم إرسال طلبك بنجاح! سيتواصل معك فريق المطور قريباً.';
        this.formData = {
          name: '',
          phone: '',
          email: '',
          unitType: this.optionsList.length > 0 ? this.optionsList[0].value : 'apartment',
        };
        setTimeout(() => {
          this.successMessage = '';
        }, 5000);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.errorMessage = err?.error?.message || err?.error?.validationErrors?.[0]?.errors?.[0] || 'فشل إرسال الطلب، يرجى المحاولة لاحقاً.';
      },
    });
  }
}
